import { cn } from "@newxsystems/shared";
import { RavioliCommonContent } from "../../../../modules/content/components/RavioliCommonContent/RavioliCommonContent";
import { RavioliRichText } from "../../../../modules/content/components/RavioliRichText/RavioliRichText";
import { RavioliSection } from "../../../../modules/content/components/RavioliSection/RavioliSection";
import type { IStatsBlockProps } from "./StatsBlock.props";

const columns = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
} as const;

export const StatsBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
}: IStatsBlockProps) => {
  if (!items?.length) return null;
  const effectiveColumnCount = Math.min(columnCount || 3, items.length) as 1 | 2 | 3 | 4;

  return (
    <RavioliSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="grid gap-8">
        <RavioliCommonContent
          alignment={alignment}
          buttons={buttons}
          description={description}
          eyebrow={eyebrow}
          heading={heading}
        />
        <div className={cn("grid gap-3", columns[effectiveColumnCount])}>
          {items.map((item, index) => {
            const featured = index === 0;
            return (
              <article
                className={cn(
                  "relative border border-primary p-6 before:absolute before:right-3 before:top-3 before:size-2",
                  featured
                    ? "bg-primary text-canvas shadow-ravioli-ink-sm before:bg-canvas"
                    : "bg-surface before:bg-primary",
                )}
                key={index}
              >
                <p
                  className={cn(
                    "font-ravioli-display text-6xl font-semibold leading-none tracking-[-0.02em] md:text-7xl",
                    featured ? "text-canvas" : "text-primary",
                  )}
                >
                  {item.value}
                </p>
                <h3
                  className={cn(
                    "mt-6 border-t pt-3 font-ravioli-body text-sm font-semibold",
                    featured ? "border-canvas/35 text-canvas" : "border-line text-foreground",
                  )}
                >
                  {item.label}
                </h3>
                {item.description ? (
                  <RavioliRichText
                    className={cn(
                      "mt-3 max-w-[32ch] text-base md:text-lg",
                      featured ? "text-canvas" : "text-ink-muted",
                    )}
                    content={item.description}
                  />
                ) : null}
              </article>
            );
          })}
        </div>
      </div>
    </RavioliSection>
  );
};

StatsBlock.displayName = "Block::StatsBlock";
